import { useGetEntity } from "@/queries/digital-files/entities.api";
import { Button, Icon, useToast } from "@elaniintc/goes-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useHeaderButton } from "@/contexts/header-button-context";
import { getNormalizedAssociates, getSectionsFromEntity } from "../utils";
import type {
  FieldType,
  RegistryFieldValueType,
} from "@/schemas/digital-files";
import {
  useGetAutoID,
  useGetRegistry,
  useSaveNewRegistry,
  useUpdateRegistry,
} from "@/queries/digital-files/registries.api";
import { useRegistryForm } from "./contexts/RegistryFormContext";
import { uploadFile, useDeleteFile } from "@/queries/files/file.api";
import { getFieldsToSave, getFilesToSave, getFilesToDelete } from "./utils";
import { useQueryClient } from "@tanstack/react-query";
import { OWNER_ROLE_ID } from "../types";
import type { SectionElement } from "./index.types";
import {
  buildFieldValuesFromRegistry,
  computeCreateModeChanges,
  computeEditModeChanges,
  transformFieldOptionsForAPI,
} from "./index.helpers";
import axios from "axios";
import type { EntityRegistryErrors } from "./components/error-summary-modal";

export interface UseEntityRegistryFormOptions {
  onError?: (errors: EntityRegistryErrors) => void;
}

export const useEntityRegistryForm = (
  onCancel: () => void,
  { onError }: UseEntityRegistryFormOptions = {},
) => {
  const { id: entityId, registryId } = useParams();
  const { data: entityData, isLoading } = useGetEntity(entityId, {
    includeFields: true,
    includeRoles: true,
  });
  const { data: registryData, isLoading: isLoadingRegistry } =
    useGetRegistry(registryId);
  const [sections, setSections] = useState<SectionElement[]>([]);
  const { setComp } = useHeaderButton();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: saveRegistry, isPending } = useSaveNewRegistry(entityId);
  const { mutateAsync: updateRegistryFields, isPending: isUpdating } =
    useUpdateRegistry(entityId);
  const { mutateAsync: deleteFileMutation } = useDeleteFile();
  const { showToast } = useToast();
  const {
    isFormValid,
    fieldValues,
    setFieldValues,
    fieldOptions,
    setFieldOptions,
    users,
    regName,
    setRegName,
    regId,
    setRegId,
    setUsers,
    registries,
    setRegistries,
    rolesList,
    setRolesList,
  } = useRegistryForm();
  const { data: autoId } = useGetAutoID(!registryId);
  const autoIdCode = autoId?.data.code;
  const registryName = registryData?.name || entityData?.name;

  // map internal role_ids to API format (multiple roles + owner flag)
  const mapUsersForAPI = useCallback(
    (list: typeof users) =>
      list.map((user) => {
        const roleIds = user.role_ids || (user.role_id ? [user.role_id] : []);
        const customRoleIds = roleIds.filter((id) => id !== OWNER_ROLE_ID);
        return {
          applicant_id: user.applicant_id,
          is_owner: roleIds.includes(OWNER_ROLE_ID),
          role_ids: customRoleIds,
        };
      }),
    [],
  );

  const uploadFieldsAfterSave = useCallback(
    async (savedFieldValues?: RegistryFieldValueType[]) => {
      if (!savedFieldValues || savedFieldValues.length === 0) return;
      const fileFields = getFilesToSave(
        sections,
        fieldValues,
        savedFieldValues,
      );
      await handleUploadFiles(fileFields);
    },
    [sections, fieldValues],
  );

  const reportSaveError = useCallback(
    (error: unknown) => {
      console.error("Error saving registry:", error);
      const title = "Registro no guardado";
      const defaultMessage = "El registro no se ha guardado correctamente.";

      if (!axios.isAxiosError(error)) {
        showToast({
          title,
          description: error instanceof Error ? error.message : defaultMessage,
          options: { type: "error" },
        });
        return;
      }

      const serverErrors = (
        error.response?.data as {
          errors?: EntityRegistryErrors;
        }
      )?.errors;
      if (serverErrors && Object.keys(serverErrors).length > 0 && onError) {
        onError(serverErrors);
        return;
      }

      showToast({
        title,
        description: error.response?.data?.message
          ? error.response?.data?.message
          : defaultMessage,
        options: { type: "error" },
      });
    },
    [showToast, onError],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const transformedOptions = transformFieldOptionsForAPI(fieldOptions);
      const fieldsWithoutFiles = getFieldsToSave(
        sections,
        fieldValues,
        registryData?.field_values || [],
      );
      const mappedUsers = mapUsersForAPI(users);

      if (registryId) {
        // Remove files that were deleted from the form
        const filesToDelete = getFilesToDelete(
          sections,
          fieldValues,
          registryData,
        );
        await handleDeleteFiles(filesToDelete);
        // Update registry fields and upload new files
        const updated = await updateRegistryFields({
          registryId,
          name: regName,
          uniqueCode: registryData?.unique_code == regId ? undefined : regId,
          registries: fieldsWithoutFiles,
          associates: mappedUsers,
          selectedOptions: transformedOptions,
          relatedRegistries: registries.map((r) => r.id),
        });
        await uploadFieldsAfterSave(updated.data.field_values ?? []);
      } else {
        const newRegistry = await saveRegistry({
          name: regName,
          uniqueCode: regId,
          registries: fieldsWithoutFiles,
          associates: mappedUsers,
          selectedOptions: transformedOptions,
          relatedRegistries: registries.map((r) => r.id),
        });
        await uploadFieldsAfterSave(newRegistry.data.field_values ?? []);
      }

      // Invalidar queries
      await queryClient.invalidateQueries({
        queryKey: ["registry-detail"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["generate-auto-id"],
      });

      showToast({
        title: "Registro guardado",
        description: "El registro se ha guardado correctamente.",
        options: { type: "success" },
      });
      goBack();
    } catch (error) {
      reportSaveError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues, fieldOptions, registries, regName, regId, sections, users]);

  const hasCreateModeChanges = useCallback(
    (fields: FieldType[]) =>
      computeCreateModeChanges(fields, {
        regName,
        users,
        registries,
        fieldValues,
        fieldOptions,
      }),
    [regName, users, registries, fieldValues, fieldOptions],
  );

  const hasEditModeChanges = useCallback(
    (fields: FieldType[]) =>
      computeEditModeChanges(fields, {
        regName,
        users,
        registries,
        fieldValues,
        fieldOptions,
        registryData,
      }),
    [regName, users, registries, fieldValues, fieldOptions, registryData],
  );

  const hasUnsavedChanges = useMemo(() => {
    if (isLoading || isLoadingRegistry) return false;
    if (!entityData) return false;
    if (Object.keys(fieldValues).length === 0) return false;

    const isCreate = !registryId;
    if (!isCreate && !registryData) return false;
    if (!isCreate && registryData && regName === "" && registryData.name !== "")
      return false;

    return isCreate
      ? hasCreateModeChanges(entityData.fields)
      : hasEditModeChanges(entityData.fields);
  }, [
    registryId,
    regName,
    fieldValues,
    registryData,
    entityData,
    isLoading,
    isLoadingRegistry,
    hasCreateModeChanges,
    hasEditModeChanges,
  ]);

  const handleUploadFiles = async (
    fileFields: Array<{ fieldId: string; value: unknown }>,
  ) => {
    // Upload files after saving the registry with the id
    if (fileFields.length > 0) {
      for (const { fieldId, value } of fileFields) {
        if (value instanceof File) {
          await uploadFile({
            entity_id: fieldId,
            file: value,
            entity_type: "expedient_base_registry_field",
            collection: "expedient_base_registry_field",
          });
        } else if (Array.isArray(value)) {
          const newFiles = value.filter((f): f is File => f instanceof File);
          // Upload new files
          await Promise.all(
            newFiles.map((file) =>
              uploadFile({
                entity_id: fieldId,
                file,
                entity_type: "expedient_base_registry_field",
                collection: "expedient_base_registry_field",
              }),
            ),
          );
        }
      }
    }
  };

  const handleDeleteFiles = async (fileIds: string[]) => {
    if (fileIds.length > 0) {
      await Promise.all(fileIds.map((fileId) => deleteFileMutation(fileId)));
    }
  };

  const goBack = useCallback(() => {
    let link = "";
    if (registryId && entityId) {
      link = `/entidad/${entityId}/detalle-registro/${registryId}`;
    } else {
      link = entityId ? `?entity=${entityId}` : "";
    }
    navigate(`/expedientes${link}`);
  }, [navigate, entityId, registryId]);

  // Handler for the cancel button
  const handleCancelClick = useCallback(() => {
    if (hasUnsavedChanges) {
      onCancel(); // Opens the modal
    } else {
      goBack();
    }
  }, [hasUnsavedChanges, onCancel, goBack]);

  useEffect(() => {
    setComp(
      <>
        <Button
          variant="quiet"
          size="xs"
          type="button"
          onClick={handleCancelClick}
        >
          Cancelar
          <Icon type="fas" name="times" />
        </Button>
        <Button
          variant="default"
          size="xs"
          type="button"
          disabled={isPending || isUpdating || !isFormValid}
          onClick={handleSubmit}
        >
          Guardar cambios
          {isPending || isUpdating ? (
            <Icon
              type="fas"
              name="spinner-third"
              className="animate-spin text-current"
            />
          ) : (
            <Icon type="fas" name="save" className="text-current" />
          )}
        </Button>
      </>,
    );

    return () => {
      setComp(null);
    };
  }, [
    setComp,
    handleSubmit,
    handleCancelClick,
    isPending,
    isUpdating,
    isFormValid,
  ]);

  useEffect(() => {
    if (!registryId && autoIdCode) {
      setRegId(autoIdCode);
    }
  }, [registryId, autoIdCode, setRegId]);

  useEffect(() => {
    if (entityData?.roles) {
      // Prepend default owner role to the list of roles from the API
      setRolesList([
        { id: OWNER_ROLE_ID, name: "Propietario", order: 0 },
        ...entityData.roles,
      ]);
    }
  }, [entityData, setRolesList]);

  useEffect(() => {
    if (entityData?.fields) {
      setSections(getSectionsFromEntity(entityData));

      // Initialize default values for new registries
      if (!registryId) {
        const defaultValues = entityData.fields.reduce(
          (acc, field) => {
            if (
              field.default_value !== null &&
              field.default_value !== undefined
            ) {
              // For identification fields, only use the default value as value
              if (field.configuration?.type === "identification") {
                acc[field.id] = {
                  value: field.default_value,
                };
              } else if (field.field_type === "MULTISELECT") {
                acc[field.id] = [
                  {
                    key: field.default_value,
                    label: field.default_value,
                  },
                ];
              } else {
                acc[field.id] = field.default_value;
              }
            }
            return acc;
          },
          {} as Record<string, unknown>, //NOSONAR: This type is necessary to allow different types of default values (string, array, object)
        );
        setFieldValues(defaultValues);
      }
    }
  }, [entityData, registryData, registryId, setFieldValues]);

  useEffect(() => {
    if (registryData) {
      if (registryData.name) setRegName(registryData.name);
      if (registryData.unique_code) setRegId(registryData.unique_code);
      if (registryData.associates?.length) {
        setUsers(getNormalizedAssociates(registryData.associates));
      }
      if (registryData.related_registries?.length) {
        setRegistries(registryData.related_registries);
      }

      if (registryData.field_values?.length) {
        setFieldValues(
          buildFieldValuesFromRegistry(registryData, entityData?.fields),
        );
      }

      // Extract selected options from dropdowns
      if (registryData.field_values && registryData.field_values.length > 0) {
        const currentFieldOptions = registryData.field_values.reduce<
          Record<
            string,
            | string
            | { id: string; table: string; display: string; source: string }
          >
        >((acc, fieldValue) => {
          const currentFieldId = fieldValue.expedient_base_entity_field_id;
          const dbRelations = registryData.data_center_relations || [];
          const dbRelation = dbRelations.find(
            (rel) =>
              rel.expedient_base_entity_field_id ===
                fieldValue.entity_field?.id &&
              rel.relation_type === "selected_option",
          );
          if (dbRelation) {
            acc[currentFieldId] = {
              display: dbRelation.display_value,
              id: dbRelation.relation_id,
              table: dbRelation.reference_name || "",
              source: "data_center",
            };
          }
          return acc;
        }, {});

        setFieldOptions(currentFieldOptions);
      }
    }
  }, [
    registryData,
    entityData,
    rolesList,
    setFieldValues,
    setRegName,
    setUsers,
    setFieldOptions,
    setRegId,
    setRegistries,
  ]);

  const handleFieldChange = (fieldId: string, value: unknown) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleOptionChange = useCallback(
    (
      fieldId: string,
      option: string,
      label?: string,
      table?: string,
      isDataCenter?: boolean,
    ) => {
      setFieldOptions((prev) => ({
        ...prev,
        [fieldId]: isDataCenter
          ? {
              id: option,
              table: table || "",
              display: label || "",
              source: "data_center",
            }
          : option,
      }));
    },
    [setFieldOptions],
  );

  return {
    isLoading,
    isLoadingRegistry,
    regName,
    setRegName,
    registryName,
    users,
    sections,
    registryData,
    regId,
    setRegId,
    entityId,
    registryId,
    fieldOptions,
    fieldValues,
    handleFieldChange,
    handleOptionChange,
  };
};
